/**
 * `/` after sign-in — a stub, and explicitly one.
 *
 * KLQ-20260819-001 puts every post-auth screen out of scope; what this proves is the
 * *seam*: the guard let the caller through, `GET /auth/me` answered, and sign-out returns
 * them to the entrance. B0 (workspace setup, MCK-20260521-010) and B1 (projects) replace it.
 *
 * `home_workspace_claimed` is read here rather than ignored because MCK-20260812-009 §E
 * makes it a rule and not a nicety: a user whose home workspace is unclaimed is shown no
 * workspace chrome at all. There is no chrome yet — so the rule shows up as the one line
 * this screen says instead of a workspace name.
 */

import { Link, useNavigate } from '@tanstack/react-router';

import { Button } from '@/components';
import { useMessages } from '@/lib/i18n';

import styles from './HomeStub.module.css';
import { useMe, useSignOut } from './queries';

export function HomeStub() {
  const t = useMessages('app');
  const navigate = useNavigate();
  const { data } = useMe();
  const signOut = useSignOut();

  // `RequireSession` renders this only once `me` has resolved, so `data` is present; the
  // fallbacks below exist for the redacted account, whose name and address are tombstoned.
  const name = data?.display_name ?? data?.email ?? '';

  return (
    <main className={styles.screen}>
      <div className={styles.column}>
        <h1 className={styles.heading}>{t('home.title')}</h1>
        <p className={styles.line}>{t('home.greeting', { name })}</p>
        <p className={styles.muted}>{t('home.placeholder')}</p>
        {/* The explicit route into B0. Only reachable by somebody the entry routing did NOT
            force there — an invited collaborator with projects of their own — because a
            session with no projects never renders this screen while unclaimed
            (`HomeRoute`). MCK-20260812-009 §B calls this one of the claim's triggers: the
            person's own behaviour asks for a company, rather than the product demanding one. */}
        {data !== undefined && !data.home_workspace_claimed ? (
          <>
            <p className={styles.muted}>{t('home.workspaceUnclaimed')}</p>
            <Link className={styles.link} to="/setup">
              {t('home.action.setUpCompany')}
            </Link>
          </>
        ) : null}
        <div>
          <Button
            variant="secondary"
            loading={signOut.isPending}
            onClick={() => {
              signOut.mutate(undefined, {
                // Navigate on settled rather than on success: a sign-out that failed still
                // means the person asked to leave, and the cookies may already be gone.
                onSettled: () => {
                  void navigate({ to: '/login', replace: true });
                },
              });
            }}
          >
            {t('home.action.signOut')}
          </Button>
        </div>
      </div>
    </main>
  );
}
